/**
 * boothSession.js — the guest's identity.
 *
 * ⚠️ This replaces Supabase auth entirely, and that is the whole point.
 *
 * A guest scans a QR code. There is no login, no account, and deliberately no
 * Supabase identity: an anonymous Supabase user carries `role: authenticated`
 * and therefore inherits every grant made to `authenticated`, which is how a
 * guest session turns into a database principal. See docs/DECISIONS.md D-04.
 *
 * Instead `/api/booth-session` mints a short-lived signed token
 * `{ sub, tenant, event, scope:'booth', exp }`, HMAC'd server-side. Worst case
 * with a stolen one: somebody makes free portraits. That is the correct blast
 * radius.
 *
 * ⚠️ THE TOKEN IS CACHED IN MEMORY ONLY, never in localStorage, and that has
 * not changed. Brief §4.26: this runs on the guest's own phone and nothing
 * about the guest EXPERIENCE may be cached per device. A token in localStorage
 * would outlive the visit for no benefit.
 *
 * ⚠️ THE DEVICE ID IS THE OTHER THING, AND IT IS PERSISTED — `D-15`, locked
 * 2026-08-21, with a written carve-out from §4.26 because an identity the
 * guest never sees is infrastructure, not experience. It is sent with the mint
 * so the token's `sub` is stable across page loads. Before this, `sub` was a
 * fresh UUID per mint and per load, which is why `RATE_LIMIT_PER_HOUR` in the
 * generator has never actually limited anybody. See lib/deviceId.js.
 */

import { loadTestSession } from './testSession';
import { getDeviceId, adoptServerDeviceId } from './deviceId';

let cached = null;          // { token, exp, tenant, event }
let inFlight = null;        // de-dupes concurrent mints

const SKEW_MS = 30_000;     // renew before expiry, not at it

function stillValid(t) {
  return t && t.token && typeof t.exp === 'number' && Date.now() < t.exp - SKEW_MS;
}

/**
 * Tenant and event come from the path: /:tenantSlug/:eventSlug
 *
 * ⚠️ The path SELECTS which config to load. It is never what a spend or a read
 * is scoped to — a guest can edit a path. The server resolves tenant and event
 * from the token on every privileged call. See D-02.
 */
export function boothScopeFromPath(pathname = window.location.pathname) {
  const [, tenant, event] = pathname.split('/');
  return { tenant: tenant || null, event: event || null };
}

export async function getBoothToken(scope = boothScopeFromPath()) {
  /* ⚠️ A TEST SESSION SUPERSEDES THE MINT, AND IT HAS TO. The console minted a
     token marked `admin` (`D-44`) and the frames were signed under ITS user id.
     Minting a fresh guest token here would produce a different `sub`, the
     generator's ownership check would refuse the caller's own uploads, and the
     bake-off would 403 halfway through with nothing to show for the money.
     Guests never have one of these, so this returns null for them and the mint
     below runs exactly as before. */
  const test = loadTestSession(scope);
  if (test) return test.token;

  if (stillValid(cached) && cached.tenant === scope.tenant && cached.event === scope.event) {
    return cached.token;
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const res = await fetch('/api/booth-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        /* ⚠️ A FALLBACK, NOT THE SOURCE OF TRUTH. The server prefers its own
           HttpOnly cookie and only reads this when the browser refused one.
           See the cookie note in netlify/functions/booth-session.js. */
        body: JSON.stringify({ ...scope, device_id: getDeviceId() }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`booth-session ${res.status} ${text.slice(0, 200)}`);
      }
      const data = await res.json();
      /* Reconcile to whatever the server decided. A phone whose localStorage
         was cleared but whose cookie survived would otherwise keep a second
         identity alive beside the real one. */
      if (data.device_id) adoptServerDeviceId(data.device_id);
      cached = { token: data.token, exp: data.exp_ms, tenant: scope.tenant, event: scope.event };
      return cached.token;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Drop the cached token — used when a call comes back 401 so the next one re-mints. */
export function clearBoothToken() { cached = null; }
